import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowsLeftRight, MapPin, Package, Lightning, Trash, Play, Stack, ArrowsCounterClockwise, Barcode, CheckCircle, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import {
  getLocations,
  getInventory,
  getVariants,
  stockMoveReasonCodes,
  quickStockMove,
  listStockMoves,
  createStockMove,
  executeStockMove,
  cancelStockMove,
  lookupLocationByCode,
  lookupItemByCode,
} from '../lib/api';
import { playSuccessTone, playErrorTone } from '../lib/audioFeedback';

const REASON_LABELS = {
  slotting: 'Slotting',
  replenishment: 'Replenishment',
  consolidation: 'Consolidation',
  damage_quarantine: 'Damage / Quarantine',
  cycle_count_correction: 'Cycle-count correction',
  other: 'Other',
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }); } catch (_) { return iso; }
};

export default function StockMoves() {
  const [locations, setLocations] = useState([]);
  const [variants, setVariants] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [reasonCodes, setReasonCodes] = useState([]);
  const [moves, setMoves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');

  // Quick Move form state
  const [qmFrom, setQmFrom] = useState('');
  const [qmTo, setQmTo] = useState('');
  const [qmMode, setQmMode] = useState('sku');
  const [qmVariant, setQmVariant] = useState('');
  const [qmQty, setQmQty] = useState(1);
  const [qmReason, setQmReason] = useState('slotting');
  const [qmNotes, setQmNotes] = useState('');
  const [qmBusy, setQmBusy] = useState(false);

  // Scan-driven Quick Move state machine
  // Steps: 'source' → 'sku' → 'qty' → 'dest' → 'submit'
  const [scanMode, setScanMode] = useState(false);
  const [scanStep, setScanStep] = useState('source');
  const [scanInput, setScanInput] = useState('');
  const [scanSrc, setScanSrc] = useState(null); // {id, code}
  const [scanSku, setScanSku] = useState(null); // {variant_id, sku_code, sku_name, available}
  const [scanQty, setScanQty] = useState('');
  const [scanDst, setScanDst] = useState(null);
  const [scanReason, setScanReason] = useState('slotting');
  const scanInputRef = React.useRef(null);

  // Auto-focus the hidden scan input every time the step changes
  useEffect(() => {
    if (scanMode && scanInputRef.current) {
      scanInputRef.current.focus();
    }
  }, [scanMode, scanStep]);

  const resetScanFlow = () => {
    setScanStep('source');
    setScanInput('');
    setScanSrc(null);
    setScanSku(null);
    setScanQty('');
    setScanDst(null);
  };

  const handleScanSubmit = async () => {
    const code = scanInput.trim();
    if (!code) return;
    setScanInput('');
    try {
      if (scanStep === 'source') {
        const r = await lookupLocationByCode(code);
        const loc = r?.data?.location;
        if (!loc) throw new Error('Source location not found');
        setScanSrc({ id: loc.id, code: loc.location_code || code });
        playSuccessTone();
        setScanStep('sku');
      } else if (scanStep === 'sku') {
        const r = await lookupItemByCode(code);
        const variant = (r?.data?.variants || [])[0];
        if (!variant) throw new Error(`No SKU found for "${code}"`);
        // Confirm there's stock at the chosen source location — prefer the lookup
        // response's per-location rows (already pre-aggregated), fall back to the
        // page's inventory cache.
        const fromLookup = (r?.data?.locations || [])
          .filter((loc) => loc.location_id === scanSrc.id && loc.variant_id === variant.id)
          .reduce((s, loc) => s + (loc.on_hand_available || 0), 0);
        const fromCache = inventory
          .filter((i) => i.location_id === scanSrc.id && i.variant_id === variant.id && i.status === 'available')
          .reduce((s, i) => s + (i.quantity || 0), 0);
        const available = fromLookup || fromCache;
        if (available <= 0) {
          playErrorTone();
          toast.error(`No "${variant.sku_code}" stock at ${scanSrc.code}`);
          return;
        }
        setScanSku({
          variant_id: variant.id,
          sku_code: variant.sku_code,
          sku_name: variant.sku_name,
          available,
        });
        playSuccessTone();
        setScanStep('qty');
      } else if (scanStep === 'qty') {
        const n = parseInt(code, 10);
        if (!Number.isFinite(n) || n <= 0) {
          playErrorTone();
          toast.error('Enter a valid positive quantity');
          return;
        }
        if (n > scanSku.available) {
          playErrorTone();
          toast.error(`Only ${scanSku.available} available`);
          return;
        }
        setScanQty(n);
        playSuccessTone();
        setScanStep('dest');
      } else if (scanStep === 'dest') {
        const r = await lookupLocationByCode(code);
        const loc = r?.data?.location;
        if (!loc) throw new Error('Destination location not found');
        if (loc.id === scanSrc.id) {
          playErrorTone();
          toast.error('Destination must differ from source');
          return;
        }
        setScanDst({ id: loc.id, code: loc.location_code || code });
        playSuccessTone();
        // Auto-submit
        await submitScanMove({ id: loc.id, code: loc.location_code || code });
      }
    } catch (e) {
      playErrorTone();
      toast.error(e?.response?.data?.detail || e.message || 'Scan failed');
    }
  };

  const submitScanMove = async (dst) => {
    setQmBusy(true);
    try {
      const res = await quickStockMove({
        from_location_id: scanSrc.id,
        to_location_id: dst.id,
        reason_code: scanReason,
        mode: 'sku',
        variant_id: scanSku.variant_id,
        quantity: parseInt(scanQty, 10),
      });
      const moved = (res?.data?.applied || []).reduce((s, a) => s + (a.moved || 0), 0);
      playSuccessTone();
      toast.success(`Moved ${moved} × ${scanSku.sku_code} · ${scanSrc.code} → ${dst.code}`);
      resetScanFlow();
      fetchAll();
    } catch (e) {
      playErrorTone();
      toast.error(e?.response?.data?.detail || 'Move failed');
      // Stay on dest step so the operator can re-scan
      setScanStep('dest');
      setScanDst(null);
    } finally {
      setQmBusy(false);
    }
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [locRes, invRes, varRes, rcRes, mvRes] = await Promise.all([
        getLocations(),
        getInventory(),
        getVariants(),
        stockMoveReasonCodes(),
        listStockMoves(),
      ]);
      setLocations(locRes?.data || []);
      setInventory(invRes?.data || []);
      setVariants(varRes?.data || []);
      setReasonCodes(rcRes?.data?.reason_codes || []);
      setMoves(mvRes?.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const variantsAtSource = useMemo(() => {
    if (!qmFrom) return [];
    const rows = inventory.filter((i) => i.location_id === qmFrom && i.status === 'available' && (i.quantity || 0) > 0);
    const byVar = new Map();
    rows.forEach((r) => {
      byVar.set(r.variant_id, (byVar.get(r.variant_id) || 0) + (r.quantity || 0));
    });
    return Array.from(byVar.entries()).map(([vid, qty]) => {
      const v = variants.find((x) => x.id === vid);
      return { variant_id: vid, sku_code: v?.sku_code || vid, sku_name: v?.name || '', qty };
    });
  }, [qmFrom, inventory, variants]);

  const filteredMoves = useMemo(() => {
    if (filterStatus === 'all') return moves;
    return moves.filter((m) => m.status === filterStatus);
  }, [moves, filterStatus]);

  const handleQuickMove = async () => {
    if (!qmFrom || !qmTo) { toast.error('Pick source + destination locations'); return; }
    if (qmFrom === qmTo) { toast.error('Source and destination must differ'); return; }
    if (qmMode === 'sku' && (!qmVariant || qmQty <= 0)) { toast.error('Pick a variant + qty'); return; }
    setQmBusy(true);
    try {
      const payload = {
        from_location_id: qmFrom,
        to_location_id: qmTo,
        reason_code: qmReason,
        mode: qmMode,
        notes: qmNotes || null,
      };
      if (qmMode === 'sku') {
        payload.variant_id = qmVariant;
        payload.quantity = parseInt(qmQty, 10);
      }
      const res = await quickStockMove(payload);
      const moved = (res?.data?.applied || []).reduce((s, a) => s + (a.moved || 0), 0);
      toast.success(`Moved ${moved} units · ${res?.data?.move_number || 'move'}`);
      setQmVariant('');
      setQmQty(1);
      setQmNotes('');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Move failed');
    } finally {
      setQmBusy(false);
    }
  };

  const handleExecute = async (id) => {
    try {
      await executeStockMove(id);
      toast.success('Move executed');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Execute failed');
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this planned move?')) return;
    try {
      await cancelStockMove(id);
      toast.success('Move cancelled');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cancel failed');
    }
  };

  const locOptions = locations.map((l) => ({
    value: l.id, label: l.location_code || l.code || l.id, warehouse: l.warehouse_id,
  }));

  return (
    <Layout>
      <div data-testid="stock-moves-page" className="p-6 max-w-6xl">
        <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-black tracking-tight flex items-center gap-2">
              <ArrowsLeftRight size={32} weight="bold" className="text-brand" />
              Stock Moves
            </h1>
            <p className="text-muted-foreground text-sm">Location-to-location transfers · same or cross-warehouse</p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchAll} data-testid="stock-moves-refresh">
            <ArrowsCounterClockwise size={14} className="mr-1" /> Refresh
          </Button>
        </div>

        <Tabs defaultValue="quick">
          <TabsList>
            <TabsTrigger value="quick" data-testid="tab-quick-move"><Lightning size={14} className="mr-1" weight="fill" /> Quick Move</TabsTrigger>
            <TabsTrigger value="planned" data-testid="tab-planned-moves"><Stack size={14} className="mr-1" /> Planned Tasks ({filteredMoves.length})</TabsTrigger>
          </TabsList>

          {/* QUICK MOVE TAB */}
          <TabsContent value="quick" className="mt-4">
            <div className="mb-3 flex items-center gap-2">
              <Button
                onClick={() => { setScanMode(false); resetScanFlow(); }}
                variant={!scanMode ? 'default' : 'outline'}
                size="sm"
                data-testid="qm-form-mode-btn"
              >Form mode</Button>
              <Button
                onClick={() => { setScanMode(true); resetScanFlow(); }}
                variant={scanMode ? 'default' : 'outline'}
                size="sm"
                className={scanMode ? 'bg-amber-500 hover:bg-amber-600' : ''}
                data-testid="qm-scan-mode-btn"
              ><Barcode size={14} className="mr-1" weight="fill" /> Scan mode</Button>
            </div>

            {scanMode ? (
              <ScanFlowPanel
                step={scanStep}
                src={scanSrc}
                sku={scanSku}
                qty={scanQty}
                dst={scanDst}
                reason={scanReason}
                onReasonChange={setScanReason}
                input={scanInput}
                onInput={setScanInput}
                onSubmit={handleScanSubmit}
                onReset={resetScanFlow}
                inputRef={scanInputRef}
                reasonCodes={reasonCodes}
                busy={qmBusy}
              />
            ) : (
            <div className="bg-white border border-border p-6 max-w-3xl">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="overline mb-1 flex items-center gap-1"><MapPin size={12} weight="fill" /> Source location</label>
                  <Select value={qmFrom} onValueChange={(v) => { setQmFrom(v); setQmVariant(''); }}>
                    <SelectTrigger data-testid="qm-from-loc"><SelectValue placeholder="Pick source..." /></SelectTrigger>
                    <SelectContent>
                      {locOptions.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="overline mb-1 flex items-center gap-1"><MapPin size={12} weight="fill" /> Destination location</label>
                  <Select value={qmTo} onValueChange={setQmTo}>
                    <SelectTrigger data-testid="qm-to-loc"><SelectValue placeholder="Pick destination..." /></SelectTrigger>
                    <SelectContent>
                      {locOptions.filter((l) => l.value !== qmFrom).map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="mt-4">
                <label className="overline mb-1">What to move</label>
                <div className="flex gap-2 mb-3">
                  <Button
                    type="button"
                    onClick={() => setQmMode('sku')}
                    variant={qmMode === 'sku' ? 'default' : 'outline'}
                    size="sm"
                    data-testid="qm-mode-sku"
                  ><Package size={14} className="mr-1" /> SKU + qty</Button>
                  <Button
                    type="button"
                    onClick={() => setQmMode('whole_location')}
                    variant={qmMode === 'whole_location' ? 'default' : 'outline'}
                    size="sm"
                    data-testid="qm-mode-whole"
                  ><Stack size={14} className="mr-1" /> Whole pallet (everything at source)</Button>
                </div>
                {qmMode === 'sku' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="md:col-span-2">
                      <Select value={qmVariant} onValueChange={setQmVariant} disabled={!qmFrom}>
                        <SelectTrigger data-testid="qm-variant"><SelectValue placeholder={qmFrom ? 'Pick a SKU available at source...' : 'Pick source location first'} /></SelectTrigger>
                        <SelectContent>
                          {variantsAtSource.length === 0 && qmFrom && <SelectItem disabled value="__empty__">No available stock at source</SelectItem>}
                          {variantsAtSource.map((v) => (
                            <SelectItem key={v.variant_id} value={v.variant_id}>
                              <span className="font-mono">{v.sku_code}</span> · {v.sku_name} <span className="text-zinc-500">({v.qty} avail)</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Input
                        type="number"
                        min={1}
                        value={qmQty}
                        onChange={(e) => setQmQty(e.target.value)}
                        placeholder="Qty"
                        data-testid="qm-qty"
                      />
                    </div>
                  </div>
                )}
                {qmMode === 'whole_location' && (
                  <div className="bg-amber-50 border border-amber-200 text-amber-900 p-3 text-xs">
                    Every available inventory line at the source location will be moved to the destination. Use this for whole-pallet moves.
                  </div>
                )}
              </div>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="overline mb-1">Reason code (required)</label>
                  <Select value={qmReason} onValueChange={setQmReason}>
                    <SelectTrigger data-testid="qm-reason"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(reasonCodes.length ? reasonCodes : Object.keys(REASON_LABELS)).map((r) => (
                        <SelectItem key={r} value={r}>{REASON_LABELS[r] || r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="overline mb-1">Notes (optional)</label>
                  <Input value={qmNotes} onChange={(e) => setQmNotes(e.target.value)} placeholder="Pallet damaged top corner..." data-testid="qm-notes" />
                </div>
              </div>

              <Button
                onClick={handleQuickMove}
                disabled={qmBusy}
                className="mt-5 h-12 px-8 bg-brand hover:bg-brand/90 text-base"
                data-testid="qm-submit"
              >
                <Lightning size={18} weight="fill" className="mr-2" />
                {qmBusy ? 'Moving...' : 'Move Now'}
              </Button>
            </div>
            )}
          </TabsContent>

          {/* PLANNED TAB */}
          <TabsContent value="planned" className="mt-4">
            <div className="mb-3 flex items-center gap-2">
              <span className="overline">Filter:</span>
              {['all', 'pending', 'completed', 'cancelled'].map((s) => (
                <button
                  key={s}
                  onClick={() => setFilterStatus(s)}
                  className={`text-xs font-bold uppercase px-2 py-1 ${filterStatus === s ? 'bg-brand text-white' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'}`}
                  data-testid={`stock-moves-filter-${s}`}
                >{s}</button>
              ))}
            </div>
            {loading ? (
              <div className="bg-white p-8 text-center text-zinc-500">Loading…</div>
            ) : filteredMoves.length === 0 ? (
              <div className="bg-white border border-border p-8 text-center text-zinc-500">No moves yet.</div>
            ) : (
              <div className="bg-white border border-border divide-y divide-zinc-100">
                {filteredMoves.map((m) => (
                  <div key={m.id} className="p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`stock-move-row-${m.move_number}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono font-black">{m.move_number}</span>
                        <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 ${
                          m.status === 'completed' ? 'bg-green-100 text-green-800' :
                          m.status === 'cancelled' ? 'bg-zinc-200 text-zinc-600' :
                          'bg-amber-100 text-amber-800'
                        }`}>{m.status}</span>
                        <span className="text-[11px] uppercase tracking-widest text-zinc-500">{REASON_LABELS[m.reason_code] || m.reason_code}</span>
                        {m.quick_move && <span className="text-[10px] bg-brand/10 text-brand font-bold px-1.5 py-0.5">QUICK</span>}
                      </div>
                      <div className="text-xs text-zinc-600 mt-1">
                        <span className="font-mono">{m.from_location_code || m.from_location_id?.slice(-6)}</span>
                        <ArrowsLeftRight size={12} className="inline mx-2" />
                        <span className="font-mono">{m.to_location_code || m.to_location_id?.slice(-6)}</span>
                        <span className="ml-3 text-zinc-400">{(m.items || []).length} item(s)</span>
                      </div>
                      {m.notes && <div className="text-xs text-zinc-500 mt-1 italic">{m.notes}</div>}
                      <div className="text-[11px] text-zinc-400 mt-1">{formatDate(m.created_at)}</div>
                    </div>
                    {m.status === 'pending' && (
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => handleExecute(m.id)} className="bg-brand hover:bg-brand/90" data-testid={`stock-move-execute-${m.move_number}`}>
                          <Play size={14} className="mr-1" weight="fill" /> Execute
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleCancel(m.id)} data-testid={`stock-move-cancel-${m.move_number}`}>
                          <Trash size={14} className="mr-1" /> Cancel
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}



/**
 * ScanFlowPanel — full-screen-ish scanner-driven Quick Move.
 *
 * 4-step state machine: source loc → SKU → qty → destination loc → auto-submit.
 * Always-focused hidden input captures HID/keyboard-wedge scanner events.
 * Audio cues fire on every successful step + on errors.
 */
function ScanFlowPanel({
  step, src, sku, qty, dst, reason, onReasonChange,
  input, onInput, onSubmit, onReset, inputRef, reasonCodes, busy,
}) {
  const stepConfig = {
    source: { label: 'Scan SOURCE location', icon: MapPin, hint: 'Aim scanner at the location barcode you\'re moving FROM' },
    sku:    { label: 'Scan SKU / barcode',   icon: Package, hint: 'Aim scanner at the item\'s barcode' },
    qty:    { label: 'Enter quantity',         icon: Stack,   hint: `Available at ${src?.code}: ${sku?.available || 0} · type qty + Enter` },
    dest:   { label: 'Scan DESTINATION location', icon: MapPin, hint: 'Aim scanner at the location barcode you\'re moving TO — auto-submits' },
  };
  const cfg = stepConfig[step] || stepConfig.source;
  const StepIcon = cfg.icon;

  return (
    <div data-testid="scan-flow-panel" className="bg-zinc-950 text-white p-6 sm:p-8 max-w-3xl">
      {/* Progress trail */}
      <div className="grid grid-cols-4 gap-2 mb-6 text-[10px] font-bold uppercase tracking-widest">
        {[
          { key: 'source', label: 'Source', value: src?.code },
          { key: 'sku',    label: 'SKU',    value: sku?.sku_code },
          { key: 'qty',    label: 'Qty',    value: qty },
          { key: 'dest',   label: 'Dest',   value: dst?.code },
        ].map((s) => {
          const done = !!s.value;
          const active = step === s.key;
          return (
            <div key={s.key} className={`p-2 border ${active ? 'border-amber-400 bg-amber-400/10' : done ? 'border-green-500 bg-green-500/10' : 'border-zinc-700'}`}>
              <div className={done ? 'text-green-300' : active ? 'text-amber-300' : 'text-zinc-500'}>{s.label}</div>
              <div className={`font-mono text-xs ${done ? 'text-white' : 'text-zinc-600'}`}>
                {done ? s.value : '—'}
              </div>
            </div>
          );
        })}
      </div>

      {/* Current step hero */}
      <div className="border-2 border-amber-400 bg-amber-400/5 p-6 mb-4" data-testid={`scan-step-${step}`}>
        <div className="flex items-center gap-3 mb-2">
          <StepIcon size={28} weight="fill" className="text-amber-400" />
          <h2 className="text-xl font-black uppercase tracking-tight">{cfg.label}</h2>
        </div>
        <p className="text-sm text-zinc-300 mb-4">{cfg.hint}</p>
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }}>
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => onInput(e.target.value)}
            type={step === 'qty' ? 'number' : 'text'}
            placeholder={step === 'qty' ? 'qty' : 'scan or type then Enter'}
            className="w-full h-14 px-4 text-2xl font-mono bg-black border-2 border-amber-400/40 focus:border-amber-400 outline-none text-amber-100"
            autoFocus
            disabled={busy}
            data-testid="scan-input"
          />
        </form>
      </div>

      {/* Reason selector */}
      <div className="flex items-center gap-3 flex-wrap mb-4">
        <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">Reason</label>
        <Select value={reason} onValueChange={onReasonChange}>
          <SelectTrigger data-testid="scan-reason" className="w-[280px] bg-zinc-900 border-zinc-700 text-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            {(reasonCodes.length ? reasonCodes : Object.keys(REASON_LABELS)).map((r) => (
              <SelectItem key={r} value={r}>{REASON_LABELS[r] || r}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button onClick={onReset} variant="outline" size="sm" className="bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-800 ml-auto" data-testid="scan-reset-btn">
          <X size={14} className="mr-1" /> Reset
        </Button>
      </div>

      {busy && (
        <div className="text-amber-300 text-xs uppercase tracking-widest animate-pulse">Submitting move…</div>
      )}
    </div>
  );
}
